import React, { useState, useEffect } from 'react';
import { 
  Container, TextField, Button, Typography, Box, List, ListItem, 
  ListItemText, Paper, InputAdornment, Alert, IconButton
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useSwimming } from '../context/SwimmingContext';
import DeleteIcon from '@mui/icons-material/Delete';
import { styled } from '@mui/material/styles';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import logoSwimteamPro from '../assets/logo_swimteampro.png';
import { motion, AnimatePresence } from 'framer-motion';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

function SwimmerRegistration() {
  const [currentSwimmer, setCurrentSwimmer] = useState('');
  const [institutionLogo, setInstitutionLogo] = useState(null);
  const [institutionName, setInstitutionName] = useState('');
  const [eventName, setEventName] = useState('');
  const navigate = useNavigate();
  const { 
    swimmers, 
    setSwimmers,
    poolLength,
    setPoolLength,
    raceDistance,
    setRaceDistance
  } = useSwimming();

  useEffect(() => {
    const savedSwimmers = localStorage.getItem('swimmers');
    const savedInstitutionName = localStorage.getItem('institutionName');
    const savedEventName = localStorage.getItem('eventName');
    const savedPoolLength = localStorage.getItem('poolLength');
    const savedRaceDistance = localStorage.getItem('raceDistance');
    const savedLogo = localStorage.getItem('institutionLogo');

    if (savedSwimmers) {
      setSwimmers(JSON.parse(savedSwimmers));
    }
    if (savedInstitutionName) {
      setInstitutionName(savedInstitutionName);
    }
    if (savedEventName) {
      setEventName(savedEventName);
    }
    if (savedPoolLength) {
      setPoolLength(parseInt(savedPoolLength));
    }
    if (savedRaceDistance) {
      setRaceDistance(parseInt(savedRaceDistance));
    }
    if (savedLogo) {
      setInstitutionLogo({ name: 'Logo Salvo' });
    }
  }, [setPoolLength, setRaceDistance, setSwimmers]);

  const handleLogoChange = (event) => {
    const file = event.target.files[0];
    if (file && (file.type === "image/png" || file.type === "image/jpeg")) {
      setInstitutionLogo(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        localStorage.setItem('institutionLogo', reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAddSwimmer = () => {
    if (currentSwimmer.trim() && !swimmers.includes(currentSwimmer.trim())) {
      const updatedSwimmers = [...swimmers, currentSwimmer.trim()];
      setSwimmers(updatedSwimmers);
      
      const currentSwimmersData = JSON.parse(localStorage.getItem('swimmersData') || '{}');
      currentSwimmersData[currentSwimmer.trim()] = {
        currentLapTime: 0,
        laps: [],
        totalDistance: 0,
        average: 0,
        poolLength: poolLength,
        raceDistance: raceDistance || poolLength
      };
      localStorage.setItem('swimmersData', JSON.stringify(currentSwimmersData));
      
      const currentSwimmersList = JSON.parse(localStorage.getItem('swimmersList') || '[]');
      currentSwimmersList.push(currentSwimmer.trim());
      localStorage.setItem('swimmersList', JSON.stringify(currentSwimmersList));
      
      setCurrentSwimmer('');
    }
  };

  const handleRemoveSwimmer = (index) => {
    const newSwimmers = swimmers.filter((_, i) => i !== index);
    setSwimmers(newSwimmers);
    
    const removedSwimmer = swimmers[index];
    const currentSwimmersData = JSON.parse(localStorage.getItem('swimmersData') || '{}');
    delete currentSwimmersData[removedSwimmer];
    localStorage.setItem('swimmersData', JSON.stringify(currentSwimmersData));
    
    const currentSwimmersList = JSON.parse(localStorage.getItem('swimmersList') || '[]');
    const updatedList = currentSwimmersList.filter(swimmer => swimmer !== removedSwimmer);
    localStorage.setItem('swimmersList', JSON.stringify(updatedList));
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleAddSwimmer();
    }
  };

  const calculateLaps = () => {
    if (!poolLength || poolLength <= 0) return 0;
    const distance = raceDistance || poolLength;
    return Math.floor(distance / poolLength);
  };

  const getValidDistance = (distance) => {
    if (!distance || !poolLength) return null;
    
    const roundedPoolLength = Math.round(poolLength * 100) / 100;
    const laps = Math.round(distance / roundedPoolLength);
    return Math.round((laps * roundedPoolLength) * 100) / 100;
  };

  const handleRaceDistanceChange = (value) => {
    const numValue = value ? Number(value) : null;
    setRaceDistance(numValue);
  };

  const handleRaceDistanceBlur = () => {
    if (raceDistance) {
      const validDistance = getValidDistance(raceDistance);
      setRaceDistance(validDistance);
    }
  };

  const laps = calculateLaps();
  const isValidSetup = poolLength > 0 && (!raceDistance || raceDistance > 0);

  const handleStartCompetition = () => {
    if (swimmers.length > 0) {
      localStorage.setItem('competitionStarted', 'true');
      localStorage.setItem('swimmers', JSON.stringify(swimmers));
      localStorage.setItem('swimmersList', JSON.stringify(swimmers));
      localStorage.setItem('institutionName', institutionName);
      localStorage.setItem('eventName', eventName);
      
      const initialSwimmersData = {};
      swimmers.forEach(swimmer => {
        initialSwimmersData[swimmer] = {
          currentLapTime: 0,
          laps: [],
          totalDistance: 0,
          average: 0,
          poolLength: poolLength,
          raceDistance: raceDistance || poolLength
        };
      });
      localStorage.setItem('swimmersData', JSON.stringify(initialSwimmersData));
      localStorage.setItem('currentSwimmerId', swimmers[0]);
      
      navigate('/competition');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Container maxWidth="md" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
        <motion.div
          initial={{ scale: 0.8 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mb: { xs: 2, sm: 4 },
              mt: { xs: 1, sm: 2 }
            }}
          >
            <Box
              component="img"
              src={logoSwimteamPro}
              alt="Logo SwimTeam Pro"
              sx={{
                width: 'auto',
                height: { xs: '50px', sm: '70px', md: '90px' },
                objectFit: 'contain'
              }}
            />
          </Box>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Paper 
            elevation={3} 
            sx={{ 
              p: { xs: 2, sm: 3, md: 4 }, 
              my: { xs: 2, sm: 4 },
              borderRadius: { xs: 2, sm: 3 }
            }}
          >
            <Typography variant="h5" component="h1" gutterBottom align="center" sx={{ mb: 4 }}>
              Configurações do Evento
            </Typography>

            <Box sx={{ mb: { xs: 3, sm: 4 } }}>
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Button
                  component="label"
                  variant="contained"
                  startIcon={<CloudUploadIcon />}
                  fullWidth
                  sx={{ mb: 1 }}
                >
                  Carregar Logotipo da Instituição
                  <VisuallyHiddenInput 
                    type="file" 
                    accept=".png,.jpg,.jpeg"
                    onChange={handleLogoChange}
                  />
                </Button>
              </motion.div>

              <Typography variant="caption" display="block" textAlign="center" sx={{ mb: 2, color: 'text.secondary' }}>
                Formatos aceitos: PNG, JPG, JPEG - Tamanho recomendado: 120x120 pixels
              </Typography>
              {institutionLogo && (
                <Typography variant="caption" display="block" textAlign="center" sx={{ mb: 2 }}>
                  Logo selecionado: {institutionLogo.name}
                </Typography>
              )}

              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
                <TextField
                  fullWidth
                  label="Nome da Instituição"
                  value={institutionName}
                  onChange={(e) => setInstitutionName(e.target.value)}
                  sx={{ mb: 2 }}
                  placeholder="Ex: CIRCUITO PAULISTA DE NATAÇÃO ACEMISTA (CPNA)"
                />

                <TextField
                  fullWidth
                  label="Nome do Evento"
                  value={eventName}
                  onChange={(e) => setEventName(e.target.value)}
                  sx={{ mb: 3 }}
                  placeholder="Ex: ACM-YMCA SÃO PAULO 2025"
                />
              </motion.div>
            </Box>

            <Typography variant="h5" component="h2" gutterBottom align="center">
              Registre as Metragens
            </Typography>

            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' }, 
              gap: { xs: 2, sm: 3 }, 
              mb: { xs: 2, sm: 3 } 
            }}>
              <Box sx={{ 
                width: { xs: '100%', sm: '40%' },
                display: 'flex',
                flexDirection: 'column',
                gap: 1
              }}>
                <TextField
                  label="Tam. Piscina"
                  type="number"
                  value={poolLength || ''}
                  onChange={(e) => setPoolLength(e.target.value ? Number(e.target.value) : '')}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">m</InputAdornment>,
                    inputProps: { 
                      step: "0.5",
                      style: { 
                        textAlign: 'center',
                        fontSize: { xs: '1rem', sm: '1.1rem' },
                        padding: '12px 65px 12px 14px',
                        color: 'currentColor'
                      }
                    }
                  }}
                  sx={{
                    '& input[type=number]': {
                      MozAppearance: 'textfield',
                      '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                        WebkitAppearance: 'inner-spin-button',
                        opacity: 1,
                        height: '100%',
                        width: '55px',
                        margin: 0,
                        position: 'absolute',
                        left: 'auto',
                        right: '0',
                        top: '0',
                        bottom: '0',
                        display: { xs: 'block', sm: 'none' }
                      }
                    },
                    '& .MuiInputBase-input': {
                      color: 'text.primary'
                    }
                  }}
                />
              </Box>

              <Box sx={{ 
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: 1
              }}>
                <TextField
                  fullWidth
                  label="Met. da Competição"
                  type="number"
                  value={raceDistance || ''}
                  onChange={(e) => handleRaceDistanceChange(e.target.value)}
                  onBlur={handleRaceDistanceBlur}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">m</InputAdornment>,
                    inputProps: { 
                      step: poolLength || 1,
                      style: { 
                        textAlign: 'center',
                        fontSize: { xs: '1rem', sm: '1.1rem' },
                        padding: '12px 65px 12px 14px',
                        color: 'currentColor'
                      }
                    }
                  }}
                  sx={{
                    '& input[type=number]': {
                      MozAppearance: 'textfield',
                      '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                        WebkitAppearance: 'inner-spin-button',
                        opacity: 1,
                        height: '100%',
                        width: '55px',
                        margin: 0,
                        position: 'absolute',
                        left: 'auto',
                        right: '0',
                        top: '0',
                        bottom: '0',
                        display: { xs: 'block', sm: 'none' }
                      }
                    },
                    '& .MuiInputBase-input': {
                      color: 'text.primary'
                    }
                  }}
                  placeholder="Digite a metragem da competição"
                  helperText="Será ajustado para o múltiplo mais próximo do tamanho da piscina"
                />
              </Box>
            </Box>

            <AnimatePresence>
              {!isValidSetup && raceDistance && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                >
                  <Alert severity="error" sx={{ mb: 2 }}>
                    A metragem total deve ser maior que zero
                  </Alert>
                </motion.div>
              )}

              {isValidSetup && laps > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                >
                  <Alert severity="info" sx={{ mb: 2 }}>
                    {`${laps} ${laps === 1 ? 'Chegada Completa' : 'Chegadas Completas'} de ${poolLength}m (Total: ${raceDistance || poolLength}m)`}
                  </Alert>
                </motion.div>
              )}
            </AnimatePresence>

            <Typography variant="h5" component="h2" gutterBottom align="center">
              Registro de Nadadores
            </Typography>
            
            <Typography variant="subtitle1" gutterBottom align="center" color="text.secondary">
              Nadadores Registrados: {swimmers.length}
            </Typography>
            
            <Box sx={{ 
              display: 'flex', 
              gap: { xs: 1, sm: 2 }, 
              mb: { xs: 3, sm: 4 },
              flexDirection: { xs: 'column', sm: 'row' }
            }}>
              <TextField
                fullWidth
                label="Nome do Nadador"
                value={currentSwimmer}
                onChange={(e) => setCurrentSwimmer(e.target.value)}
                onKeyPress={handleKeyPress}
                variant="outlined"
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: { xs: 1, sm: 2 }
                  }
                }}
              />
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Button
                  variant="contained"
                  onClick={handleAddSwimmer}
                  disabled={!currentSwimmer.trim() || !isValidSetup}
                  sx={{ 
                    minWidth: { xs: '100%', sm: '120px' },
                    height: { xs: '45px', sm: 'auto' }
                  }}
                >
                  Adicionar
                </Button>
              </motion.div>
            </Box>

            <List sx={{ 
              bgcolor: 'background.default', 
              borderRadius: { xs: 1, sm: 2 }, 
              mb: { xs: 2, sm: 3 },
              maxHeight: { xs: '200px', sm: '300px' },
              overflow: 'auto'
            }}>
              <AnimatePresence>
                {swimmers.map((swimmer, index) => (
                  <motion.div
                    key={swimmer}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ delay: index * 0.1 }}
                    style={{ overflow: 'hidden' }}
                  >
                    <Box component="div">
                      <ListItem
                        sx={{ overflow: 'hidden' }}
                        secondaryAction={
                          <motion.div 
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            style={{ display: 'inline-flex' }}
                          >
                            <IconButton edge="end" onClick={() => handleRemoveSwimmer(index)}>
                              <DeleteIcon />
                            </IconButton>
                          </motion.div>
                        }
                      >
                        <ListItemText 
                          primary={`${index + 1}. ${swimmer}`}
                          sx={{ 
                            '& .MuiListItemText-primary': { 
                              fontWeight: 500,
                              color: 'primary.main'
                            },
                            overflow: 'hidden'
                          }}
                        />
                      </ListItem>
                    </Box>
                  </motion.div>
                ))}
              </AnimatePresence>
            </List>

            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                disabled={swimmers.length === 0 || !isValidSetup}
                onClick={handleStartCompetition}
                sx={{ 
                  mt: { xs: 1, sm: 2 },
                  py: { xs: 1.2, sm: 1.5 },
                  fontSize: { xs: '1rem', sm: '1.1rem' }
                }}
              >
                Iniciar Competição
              </Button>
            </motion.div>
          </Paper>
        </motion.div>
      </Container>
    </motion.div>
  );
}

export default SwimmerRegistration;